import { MessageType, SOURCE_TAG } from "./protocol.js";
import type {
  OnboardingClient,
  OnboardingConfig,
  UploadOptions,
  UploadJourney,
  SignupOptions,
  SignupJourney,
  SignupSubmitData,
  LaunchOptions,
  LaunchJourney,
  ContactOptions,
  ContactJourney,
  PartnerApplicationOptions,
  PartnerApplicationJourney,
  SessionState,
  LaunchTokenOptions,
  LaunchTokenResult,
} from "./types.js";

/**
 * Read a File into an ArrayBuffer, using arrayBuffer() if available
 * or falling back to FileReader for older environments.
 */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

let counter = 0;
function nextRequestId(): string {
  return `req_${++counter}_${Date.now().toString(36)}`;
}

interface PendingRequest {
  resolve: (data: unknown) => void;
  reject: (error: unknown) => void;
  onProgress?: (data: unknown) => void;
}

interface ShellConnection {
  iframe: HTMLIFrameElement;
  listener: (e: MessageEvent) => void;
  pending: Map<string, PendingRequest>;
  ready: Promise<void>;
}

function createShellConnection(
  origin: string,
  getPostHogDistinctId?: () => string | null,
): ShellConnection {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.position = "absolute";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = `${origin}/onboard/shell`;

  const pending = new Map<string, PendingRequest>();
  let resolveReady: () => void;
  const ready = new Promise<void>((r) => {
    resolveReady = r;
  });

  const listener = (e: MessageEvent) => {
    if (e.origin !== origin) return;
    if (!e.data || e.data.source !== SOURCE_TAG) return;

    const { type, requestId, payload } = e.data;

    if (type === MessageType.READY) {
      // Send INIT with PostHog distinct ID
      const posthogDistinctId = getPostHogDistinctId?.() ?? null;
      iframe.contentWindow?.postMessage(
        {
          type: MessageType.INIT,
          source: SOURCE_TAG,
          payload: { posthogDistinctId },
        },
        origin,
      );
      resolveReady();
      return;
    }

    if (type === MessageType.ERROR && requestId) {
      const req = pending.get(requestId);
      if (req) {
        pending.delete(requestId);
        req.reject(payload);
      }
      return;
    }

    if (type === MessageType.PROGRESS && requestId) {
      const req = pending.get(requestId);
      if (req?.onProgress) {
        req.onProgress(payload);
      }
      return;
    }

    // Terminal response types — resolve and clean up
    if (requestId) {
      const req = pending.get(requestId);
      if (req) {
        pending.delete(requestId);
        req.resolve(payload);
      }
    }
  };

  window.addEventListener("message", listener);
  document.body.appendChild(iframe);

  return { iframe, listener, pending, ready };
}

function destroyConnection(conn: ShellConnection): void {
  window.removeEventListener("message", conn.listener);
  conn.iframe.remove();
  // Reject any outstanding requests
  for (const [, req] of conn.pending) {
    req.reject(new Error("Connection destroyed"));
  }
  conn.pending.clear();
}

function sendMessage(
  conn: ShellConnection,
  type: string,
  payload: unknown,
  origin: string,
  transferables?: Transferable[],
): string {
  const requestId = nextRequestId();
  const message = { type, source: SOURCE_TAG, requestId, payload };
  if (transferables?.length) {
    conn.iframe.contentWindow?.postMessage(message, origin, transferables);
  } else {
    conn.iframe.contentWindow?.postMessage(message, origin);
  }
  return requestId;
}

export function createOnboarding(config: OnboardingConfig): OnboardingClient {
  const { origin, getPostHogDistinctId } = config;
  let sharedConnection: ShellConnection | null = null;

  function getConnection(): ShellConnection {
    if (!sharedConnection) {
      sharedConnection = createShellConnection(origin, getPostHogDistinctId);
    }
    return sharedConnection;
  }

  function cleanupRequests(requestIds: Set<string>): void {
    if (!sharedConnection) return;
    for (const rid of requestIds) {
      const req = sharedConnection.pending.get(rid);
      if (req) {
        sharedConnection.pending.delete(rid);
        req.reject(new Error("Journey unmounted"));
      }
    }
    requestIds.clear();
  }

  return {
    getSession(): Promise<SessionState> {
      const conn = getConnection();
      return conn.ready.then(() => {
        return new Promise<SessionState>((resolve, reject) => {
          const requestId = sendMessage(conn, MessageType.GET_SESSION, {}, origin);
          conn.pending.set(requestId, {
            resolve: (data) => {
              resolve(data as SessionState);
            },
            reject: (err) => {
              reject(err);
            },
          });
        });
      });
    },

    launchToken(options: LaunchTokenOptions): Promise<LaunchTokenResult> {
      const conn = getConnection();
      return conn.ready.then(() => {
        return new Promise<LaunchTokenResult>((resolve, reject) => {
          const requestId = sendMessage(conn, MessageType.LAUNCH_TOKEN_REQUEST, { target: options.target }, origin);
          conn.pending.set(requestId, {
            resolve: (data) => resolve(data as LaunchTokenResult),
            reject: (err) => reject(err),
          });
        });
      });
    },

    upload(options: UploadOptions): UploadJourney {
      const conn = getConnection();
      const myRequests = new Set<string>();

      return {
        start(file: File): void {
          conn.ready.then(async () => {
            const buffer = await readFileAsArrayBuffer(file);
            const requestId = sendMessage(
              conn,
              MessageType.START_UPLOAD,
              { fileName: file.name, fileType: file.type, buffer },
              origin,
              [buffer],
            );
            myRequests.add(requestId);
            conn.pending.set(requestId, {
              resolve: (data) => {
                myRequests.delete(requestId);
                options.onComplete?.(data as Parameters<NonNullable<typeof options.onComplete>>[0]);
              },
              reject: (err) => {
                myRequests.delete(requestId);
                options.onError?.(err as Parameters<NonNullable<typeof options.onError>>[0]);
              },
              onProgress: (data) => {
                options.onProgress?.(data as Parameters<NonNullable<typeof options.onProgress>>[0]);
              },
            });
          });
        },
        unmount(): void {
          cleanupRequests(myRequests);
        },
      };
    },

    signup(options: SignupOptions): SignupJourney {
      const conn = getConnection();
      const myRequests = new Set<string>();

      return {
        submit(data: SignupSubmitData): void {
          conn.ready.then(() => {
            const requestId = sendMessage(conn, MessageType.SUBMIT_SIGNUP, data, origin);
            myRequests.add(requestId);
            conn.pending.set(requestId, {
              resolve: (result) => {
                myRequests.delete(requestId);
                options.onSuccess?.(result as Parameters<NonNullable<typeof options.onSuccess>>[0]);
              },
              reject: (err) => {
                myRequests.delete(requestId);
                options.onError?.(err as Parameters<NonNullable<typeof options.onError>>[0]);
              },
            });
          });
        },
        unmount(): void {
          cleanupRequests(myRequests);
        },
      };
    },

    launch(options: LaunchOptions): LaunchJourney {
      const conn = getConnection();
      const myRequests = new Set<string>();

      return {
        start(data: { accountId: string; jobId: string }): void {
          conn.ready.then(() => {
            const requestId = sendMessage(conn, MessageType.LAUNCH_EDITOR, data, origin);
            myRequests.add(requestId);
            conn.pending.set(requestId, {
              resolve: (result) => {
                myRequests.delete(requestId);
                options.onReady?.(result as Parameters<NonNullable<typeof options.onReady>>[0]);
              },
              reject: (err) => {
                myRequests.delete(requestId);
                options.onError?.(err as Parameters<NonNullable<typeof options.onError>>[0]);
              },
            });
          });
        },
        unmount(): void {
          cleanupRequests(myRequests);
        },
      };
    },

    contact(options: ContactOptions): ContactJourney {
      const conn = getConnection();
      const myRequests = new Set<string>();

      return {
        submit(data: { name: string; email: string; subject: string; message: string }): void {
          conn.ready.then(() => {
            const requestId = sendMessage(conn, MessageType.SUBMIT_CONTACT, data, origin);
            myRequests.add(requestId);
            conn.pending.set(requestId, {
              resolve: (result) => {
                myRequests.delete(requestId);
                options.onSuccess?.(result as Parameters<NonNullable<typeof options.onSuccess>>[0]);
              },
              reject: (err) => {
                myRequests.delete(requestId);
                options.onError?.(err as Parameters<NonNullable<typeof options.onError>>[0]);
              },
            });
          });
        },
        unmount(): void {
          cleanupRequests(myRequests);
        },
      };
    },

    partnerApplication(options: PartnerApplicationOptions): PartnerApplicationJourney {
      const conn = getConnection();
      const myRequests = new Set<string>();

      return {
        submit(data: {
          companyName: string;
          contactName: string;
          email: string;
          phone?: string;
          website?: string;
          partnerType?: string;
          message: string;
        }): void {
          conn.ready.then(() => {
            const requestId = sendMessage(
              conn,
              MessageType.SUBMIT_PARTNER_APPLICATION,
              data,
              origin,
            );
            myRequests.add(requestId);
            conn.pending.set(requestId, {
              resolve: (result) => {
                myRequests.delete(requestId);
                options.onSuccess?.(result as Parameters<NonNullable<typeof options.onSuccess>>[0]);
              },
              reject: (err) => {
                myRequests.delete(requestId);
                options.onError?.(err as Parameters<NonNullable<typeof options.onError>>[0]);
              },
            });
          });
        },
        unmount(): void {
          cleanupRequests(myRequests);
        },
      };
    },

    destroy(): void {
      if (sharedConnection) {
        destroyConnection(sharedConnection);
        sharedConnection = null;
      }
    },
  };
}
